import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import '../../../node_modules/bootstrap/dist/css/bootstrap.min.css';
import TableContainer from '../../components/Common/TableContainer';
import * as Yup from 'yup';
import { useFormik } from 'formik';

//import components
import Breadcrumbs from '../../components/Common/Breadcrumb';
//Import Flatepicker
import 'flatpickr/dist/themes/material_blue.css';
import Flatpickr from 'react-flatpickr';
import { useTranslation } from 'react-i18next';

import {
  addNewGroup as onAddNewGroup,
  updateGroup as onUpdateGroup,
} from 'store/actions';

import { PaymentStatus, PaymentMethod } from './GroupCol';
import { useMutation, useQueryClient } from 'react-query';

//redux
import { useSelector, useDispatch } from 'react-redux';
import GroupsModal from './GroupsModal';

import {
  Col,
  Row,
  Card,
  CardBody,
} from 'reactstrap';
import moment from 'moment';
import { ToastContainer } from 'react-toastify';
import { getAssignedGroupTransfersApi } from 'api/group';
import { useRole } from 'hooks/useRole';
import { useAuth } from 'hooks/useAuth';
import { getSearchColumns, sumArray } from 'utils/tools';
import { setTransferSummary } from 'store/actions';
import { getGroupTransferStatusCountsApi } from 'api/report';
import MainFilter from 'components/Common/MainFilter';
import StatCards from 'components/Common/StatCards';

function GroupTransfers() {
  const { isAuthenticated } = useAuth();
  const dispatch = useDispatch();
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { transferSummary } = useSelector((state) => ({
    transferSummary: state.reports.transfers,
  }));
  const {
    isFacilitator,
    isSiteCord,
    isCountyCoordinator,
    isRegionalCountyCoordinator,
    isClusterLeader,
    isFinance,
    isDirector,
    isAdmin,
    isSuperAdmin,
    isData,
    addresses,
  } = useRole();
  const { wardId, constituencyId, countyId, regionId } = isAuthenticated
    ? addresses[0]
    : {};

  const [addressData, setAddressData] = useState({});

  const queryClient = useQueryClient();
  const dateFormat = 'DD-MM-YYYY';
  const [pagination, setPagination] = useState({
    pageSize: 10,
    pageNumber: 1,
    sortBy: 'id',
    ascendingSort: false,
    wardIds: getSearchColumns()?.find((t) => t?.name === 'wardIds')?.value,
    searchColumns: [
      ...(isFacilitator
        ? [
          {
            name: 'wardId',
            value: wardId,
          },
        ]
        : []),
      ...(isCountyCoordinator
        ? [
          {
            name: 'countyId',
            value: countyId,
          },
          {
            name: 'countyIds',
            values: addresses?.map((c) => c.countyId),
          },
        ]
        : []),
      ...(isRegionalCountyCoordinator
        ? [
          {
            name: 'regionIds',
            values: addresses?.map((c) => c.regionId),
          },
        ]
        : []),
      ...(isClusterLeader
        ? [
          {
            name: 'regionId',
            value: regionId,
          },
          {
            name: 'constituencyIds',
            values: addresses?.map((c) => c.constituencyId),
          },
        ]
        : []),
      ...(isSiteCord
        ? [
          {
            name: 'constituencyId',
            value: constituencyId,
          },
          {
            name: 'constituencyIds',
            values: addresses?.map((c) => c.constituencyId),
          },
        ]
        : []),
      {
        name: 'endDate',
        value: moment().format(dateFormat),
      },
      {
        name: 'startDate',
        value: moment().startOf('year').format(dateFormat),
      },
    ],
  });

  const {
    mutate: mutateTransferCounts,
    isLoading: isLoadingCounts,
    data,
  } = useMutation((payload) => getGroupTransferStatusCountsApi(payload), {
    onSuccess: (res) => {
      dispatch(setTransferSummary(res?.data));
    },
    onSettled: () => {
      queryClient.invalidateQueries('get-all-counties');
    },
  });

  const {
    mutate: mutateTransfers,
    isLoading: isLoadingGroups,
    data: groupsData,
  } = useMutation((payload) => {
    // console.log(payload);
    let areaParams = {};
    if (isFacilitator && wardId) {
      areaParams = { wardId };
    } else if (isSiteCord && constituencyId) {
      areaParams = { subCountyId: constituencyId };
    } else if (isCountyCoordinator && countyId) {
      areaParams = { countyId };
    } else if (isClusterLeader && regionId) {
      areaParams = { regionId };
    }

    if ((isClusterLeader || isFacilitator || isCountyCoordinator || isSiteCord) && Object.keys(areaParams).length === 0) {
      throw new Error("Invalid area parameters: No valid area ID found.");
    }
    areaParams.startDate = pagination.searchColumns.find(s => s.name === 'startDate')?.value;
    areaParams.endDate = pagination.searchColumns.find(s => s.name === 'endDate')?.value;
    areaParams.groupName = pagination.searchColumns.find(s => s.name === 'groupName')?.value;
    areaParams.pageNumber = payload?.pageNumber || 0;
    areaParams.pageSize = payload?.pageSize || 50;

    return getAssignedGroupTransfersApi(areaParams);
  }, {
    onSuccess: (res) => {
      res.pages > 0 && setPages(res.pages);
    },
    onSettled: () => {
      setLoading(false);
      queryClient.invalidateQueries('get-all-counties');
    },
  });

  const groupsCardData = [
    {
      title: 'New Transfer Requests',
      iconClass: 'bx bx-group',
      description: transferSummary.length
        ? sumArray(transferSummary, 'totalTransferRequests').toLocaleString()
        : 0,
    },
    {
      title: 'Approved Requests',
      iconClass: 'bx bx-trophy',
      description: transferSummary.length
        ? sumArray(transferSummary, 'totalApprovedRequests').toLocaleString()
        : 0,
    },
    {
      title: 'Rejected Requests',
      iconClass: 'bx bx-dislike',
      description: transferSummary.length
        ? sumArray(transferSummary, 'totalRejectedRequests').toLocaleString()
        : 0,
    },
  ];

  //meta title
  document.title = 'Groups | SUN Welfare Administration System';

  const [modal, setModal] = useState(false);
  const [modal1, setModal1] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [order, setGroup] = useState(null);

  // validation
  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      orderId: (order && order.orderId) || '',
      billingName: (order && order.billingName) || '',
      orderdate: (order && order.orderdate) || '',
      total: (order && order.total) || '',
      paymentStatus: (order && order.paymentStatus) || 'Paid',
      badgeclass: (order && order.badgeclass) || 'success',
      paymentMethod: (order && order.paymentMethod) || 'Mastercard',
    },
    validationSchema: Yup.object({
      orderId: Yup.string()
        .matches(/[0-9\.\-\s+\/()]+/, 'Please Enter Valid Group Id')
        .required('Please Enter Your Group Id'),
      billingName: Yup.string().required('Please Enter Your Billing Name'),
      orderdate: Yup.string().required('Please Enter Your Group Date'),
      total: Yup.string()
        .matches(/[0-9\.\-\s+\/()]+/, 'Please Enter Valid Amount')
        .required('Total Amount'),
      paymentStatus: Yup.string().required('Please Enter Your Payment Status'),
      badgeclass: Yup.string().required('Please Enter Your Badge Class'),
      paymentMethod: Yup.string().required('Please Enter Your Payment Method'),
    }),
    onSubmit: (values) => {
      if (isEdit) {
        const updateGroup = {
          id: order ? order.id : 0,
          orderId: values.orderId,
          billingName: values.billingName,
          orderdate: values.orderdate,
          total: values.total,
          paymentStatus: values.paymentStatus,
          paymentMethod: values.paymentMethod,
          badgeclass: values.badgeclass,
        };
        // update order
        dispatch(onUpdateGroup(updateGroup));
        validation.resetForm();
      } else {
        const newGroup = {
          id: Math.floor(Math.random() * (30 - 20)) + 20,
          orderId: values['orderId'],
          billingName: values['billingName'],
          orderdate: values['orderdate'],
          total: values['total'],
          paymentStatus: values['paymentStatus'],
          paymentMethod: values['paymentMethod'],
          badgeclass: values['badgeclass'],
        };
        // save new order
        dispatch(onAddNewGroup(newGroup));
        validation.resetForm();
      }
      toggle();
    },
  });

  const toggleViewModal = () => setModal1(!modal1);

  useEffect(() => {
    mutateTransferCounts({
      fromDate: moment().startOf('month').format('DD-MM-YYYY'),
      toDate: moment().format('DD-MM-YYYY'),
      address: addresses[0],
    });
  }, []);

  useEffect(() => {
    mutateTransfers({
      ...pagination,
      areaId:
        isAdmin || isSuperAdmin
          ? ''
          : getSearchColumns()?.find((t) => t?.name === 'wardIds')?.value,

      pageNumber: pagination.pageNumber - 1,
    });
  }, [pagination]);

  const onChangePagination = ({ page, limit }, filters, sorter) => {
    console.log({ page, limit }, filters, sorter);
    let searchColumns = isEmpty(filters)
      ? pagination.searchColumns
      : [
        ...pagination.searchColumns,
        ...(filters?.groupName
          ? [
            {
              name: 'groupName',
              value: filters?.groupName,
            },
          ]
          : []),
        ...(filters?.groupNumber
          ? [
            {
              name: 'groupNumber',
              value: filters?.groupNumber,
            },
          ]
          : []),
      ];
    setPagination({
      ...pagination,
      pageNumber: page || pagination.pageNumber,
      pageSize: limit || pagination.pageSize,
      searchColumns,
    });
  };

  const toggle = () => {
    if (modal) {
      setModal(false);
      setGroup(null);
    } else {
      setModal(true);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: () => (
          <div className="form-check font-size-16">
            <input className="form-check-input" type="checkbox" id="checkAll" />
            <label className="form-check-label" htmlFor="checkAll"></label>
          </div>
        ),
        accessor: '#',
        width: '20px',
        filterable: true,
        Cell: (cellProps) => (
          <div className="form-check font-size-16">
            <input className="form-check-input" type="checkbox" id="checkAll" />
            <label className="form-check-label" htmlFor="checkAll"></label>
          </div>
        ),
      },
      {
        Header: 'ID',
        accessor: 'id',
        filterable: false,
        Cell: (cellProps) => {
          return <PaymentMethod {...cellProps} />;
        },
      },
      {
        Header: 'DATE',
        accessor: 'createdDate',
        filterable: true,
        Cell: (cellProps) => {
          const date = moment(cellProps.value);
          const formattedDate = date.isValid()
            ? date.format('DD MMM YYYY - h:mm A')
            : '';
          return <span>{formattedDate}</span>;
        },
      },
      {
        Header: 'TRANSFER FROM',
        accessor: 'fromWardName',
        filterable: true,
        Cell: (cellProps) => {
          return <PaymentMethod {...cellProps} />;
        },
      },
      {
        Header: 'TRANSFER TO',
        accessor: 'toWardName',
        filterable: true,
        Cell: (cellProps) => {
          return <PaymentMethod {...cellProps} />;
        },
      },
      {
        Header: 'Group NAME',
        accessor: 'groupName',
        filterable: true,
        Cell: (cellProps) => {
          return <PaymentMethod {...cellProps} />;
        },
      },

      {
        Header: 'STATUS',
        accessor: 'status',
        Cell: (cellProps) => {
          return <PaymentStatus {...cellProps} />;
        },
      },
      {
        Header: 'COMMENTS',
        accessor: 'comments',
        Cell: (cellProps) => {
          return <PaymentMethod {...cellProps} />;
        },
      },
    ],
    [],
  );

  const onSearch = (data) => {
    const searchColumns = [];
    if (!isEmpty(data)) {
      if (isFacilitator) {
        searchColumns.push({ name: 'wardId', value: address?.wardId });
      }
      if (isSiteCord && data.wardId) {
        searchColumns.push(data?.wardId);
      } else {
        if (
          (isClusterLeader ||
            isFinance ||
            isDirector ||
            isAdmin ||
            isSuperAdmin) &&
          data?.regionId
        ) {
          searchColumns.push(data?.regionId);
        }
        if (data.wardId) {
          searchColumns.push(data?.wardId);
        }
        if (data.constituencyId) {
          searchColumns.push(data?.constituencyId);
        }
        if (data.countyId) {
          searchColumns.push(data?.countyId);
        }
      }
      data.startDate && searchColumns.push(data.startDate);
      data.endDate && searchColumns.push(data.endDate);
    }

    setPagination({ ...pagination, searchColumns });
    let add = {};
    searchColumns.forEach((a, i) => {
      add[a.name] = a.value;
    });
    setAddressData(add);
  };

  return (
    <React.Fragment>
      <GroupsModal isOpen={modal1} toggle={toggleViewModal} />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Home" breadcrumbItem="Group Transfers" />
          <StatCards items={groupsCardData} loading={isLoadingCounts} md="4" />
          <Card>
            <CardBody className="pb-0">
              <MainFilter onSearch={onSearch} pagination={pagination} showDateFilter  inlineActions />
            </CardBody>
          </Card>
          <Row>
            <Col xs="12">
              <Card>
                <CardBody>
                  <TableContainer
                    pageSizeAtBottom
                    columns={columns}
                    data={groupsData?.list?.reverse() || []}
                    isGlobalFilter={true}
                    isAddOptions={true}
                    hasCreate={!isData}
                    hasSearch
                    pages={pages}
                    isPagination={true}
                    filterable={false}
                    iscustomPageSizeOptions={true}
                    // showEdit
                    loading={isLoadingGroups}
                    showView
                    resource="group"
                    addText="New Transfer"
                    tableClass="align-middle table-nowrap table-check"
                    theadClass="table-light"
                    pagination="pagination pagination-rounded justify-content-end mb-2"
                    paginationData={pagination}
                    onChangePagination={onChangePagination}
                    searchByNames={[
                      {
                        value: 'groupName',
                        text: t('groups.groupName'),
                      },
                      {
                        value: 'groupNumber',
                        text: t('groups.groupNumber'),
                      },
                    ]}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
}
GroupTransfers.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
};

export default GroupTransfers;
